import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { lifeAreaOtherID } from 'shared/types/orderable-section';

import { ARCHIVED_SECTION_ID } from '../constants';

export type Options = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
};

export const useLifeAreaGoalListTree = (
  goals: Goal[],
  lifeAreas: LifeArea[],
  { hideArchived, hideCompleted }: Options,
) => {
  const { t } = useTranslation();

  return useMemo<Record<ID, TreeItem>>(() => {
    const activeGoals: Goal[] = [];
    const archivedGoals: Goal[] = [];

    goals.forEach((goal) => {
      if (goal.archivedAt) {
        if (!hideArchived) {
          archivedGoals.push(goal);
        }
        return;
      }
      // Completed goals are now treated as active in the tree structure
      if (!hideCompleted || !goal.completedAt) {
        activeGoals.push(goal);
      }
    });

    const treeItems: Record<ID, TreeItem> = {};

    lifeAreas.forEach((lifeArea) => {
      treeItems[lifeArea.id] = {
        id: lifeArea.id,
        type: TaskListItemTypes.LifeArea,
        name: lifeArea.name,
        children: [],
        isFolder: true,
      };
    });

    // Ensure 'other' exists for LifeArea groping
    treeItems[lifeAreaOtherID] = {
      id: lifeAreaOtherID,
      type: TaskListItemTypes.LifeArea,
      name: t('general.sections.life-areas.other.title'),
      children: [],
      isFolder: true,
    };

    goals.forEach((goal) => {
      treeItems[goal.id] = {
        id: goal.id,
        type: TaskListItemTypes.Goal,
        name: goal.name,
        children: [],
        isFolder: true,
      };
    });

    activeGoals.forEach((goal) => {
      const parentIds = goal.parentIds || [];
      // Find the first parent that exists (closest ancestor)
      const parentId = [...parentIds].reverse().find((id) => treeItems[id]);

      if (parentId) {
        // It's a sub-goal, add to parent goal
        treeItems[parentId].children?.push(goal.id);
        return;
      }

      // It's a top-level goal (in this context)
      const lifeAreaId = goal.lifeAreaId || lifeAreaOtherID;
      const targetId = treeItems[lifeAreaId] ? lifeAreaId : lifeAreaOtherID;
      treeItems[targetId].children?.push(goal.id);
    });

    // Completed and Archived Sections

    if (archivedGoals.length) {
      treeItems[ARCHIVED_SECTION_ID] = {
        id: ARCHIVED_SECTION_ID,
        type: TaskListItemTypes.LifeArea,
        name: 'Archived',
        children: archivedGoals.map((g) => g.id),
        isFolder: true,
      };
    }

    // Append Sections to Root
    const rootChildren: ID[] = [];
    const children = lifeAreas.reduce<ID[]>((acc, lifeArea) => {
      if (treeItems[lifeArea.id].children?.length) {
        acc.push(lifeArea.id);
      }
      return acc;
    }, []);

    if (treeItems[lifeAreaOtherID]?.children?.length) {
      children.push(lifeAreaOtherID);
    }

    rootChildren.push(...children);

    if (archivedGoals.length) {
      rootChildren.push(ARCHIVED_SECTION_ID);
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TaskListItemTypes.Root,
        children: rootChildren,
      },
      ...treeItems,
    };
  }, [goals, lifeAreas, t, hideArchived, hideCompleted]);
};
